import { html, TemplateResult } from 'lit';
import { customElement, query } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ANTI_EXPLOIT_APP_FORM } from '../../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../../../ui/gzForm/formGroup/form-group';
import { InputWidthValues } from '../../../../../../../../../../ui/gzInput';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import {
  AntimalwareAntiExploitDetectionTechniquesName,
  AntimalwareAntiExploitSystemWideDetectionsLinuxAction,
} from '@nirvana/shared/dtos/types/policies/common';
import { AntimalwareAntiExploitUiApplication } from '../../../../../../../utils/uiSettings/antimalware/antimalware.types';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../../../../../ui/gzChipsGeneratorInput';
import { FontSizes, FontWeights } from '../../../../../../../../../../types/common/types';
import {
  AntimalwareAntiExploitDetectionTechniquesKey,
  columns,
  convertDbValueToFormValue,
  convertFormValueToDbValue,
  rows,
} from './complex-selection-group-config';
import { GzSelectableTableRowGroupWebComponent } from '../../../../../../../../../../ui/gzSelectableTableRowGroup';
import { ButtonFontSizes, ButtonHeights, ButtonTypes } from '../../../../../../../../../../ui/gzButton/types';
import { toggleDisabled } from '../../../../../../../../../../utils/htmlElement.utils';
import _ from 'lodash';
import { ComplexSelectionFormValue } from '../../../../../../../../../../ui/gzSelectableTableRowGroup/types';
import { getAntiExploitApplicationStatus } from '../utils/converters';
import { getAntimalwareAntiExploitDetectionTechniquesDefaultValue } from '../../../../../../../utils/uiSettings/antimalware/antimalware.values';

@customElement(WEB_COMPONENT_POLICY_PANEL_ANTIMALWARE_ANTI_EXPLOIT_APP_FORM)
export class GzAntimalwareAntiExploitAppFormWebComponent extends GzPolicyFullPageWindowFormWebComponent<AntimalwareAntiExploitUiApplication> {
  // general section
  @query('#application-name-field')
  private applicationNameField: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#process-name-field')
  private processNameField: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent, string[]>;

  // settings section
  @query('#antimalware-anti-exploit-detection-techniques-field')
  private exploitDetectionTechniquesField: GzFormFieldWebComponent<
    GzSelectableTableRowGroupWebComponent<
      AntimalwareAntiExploitDetectionTechniquesKey,
      AntimalwareAntiExploitDetectionTechniquesName
    >
  >;

  public constructor(private isCustomApp: boolean, value?: AntimalwareAntiExploitUiApplication) {
    super(value);
  }

  static get styles() {
    return styles;
  }

  public setError(error: string) {
    this.applicationNameField.setError(error);
  }

  protected getConvertedFormValue() {
    const formValue = _.cloneDeep(this.formGroup.value);

    // convert exploit detection techniques
    const exploitDetectionTechniques = _.get(
      formValue,
      ['exploitDetectionTechniques'],
      {}
    ) as ComplexSelectionFormValue<
      AntimalwareAntiExploitDetectionTechniquesKey,
      AntimalwareAntiExploitSystemWideDetectionsLinuxAction
    >;
    const exploitDetectionTechniquesConverted = convertFormValueToDbValue(exploitDetectionTechniques);
    _.set(formValue, ['exploitDetectionTechniques'], exploitDetectionTechniquesConverted);

    // calculate status
    _.set(
      formValue,
      ['status'],
      getAntiExploitApplicationStatus(this.isCustomApp, exploitDetectionTechniquesConverted)
    );

    return formValue;
  }

  createFormGroup(): void {
    this.applicationNameField.Validators = [RequiredValidator(translateFn('Required field'))];
    this.processNameField.Validators = [RequiredValidator(translateFn('Required field'))];

    this.formGroup = FormGroup.build({
      applicationName: this.applicationNameField,
      processName: this.processNameField,
      exploitDetectionTechniques: this.exploitDetectionTechniquesField,
    });
  }

  setFormGroupValue() {
    if (!this.isCustomApp) {
      toggleDisabled(this.applicationNameField, true);
      toggleDisabled(this.processNameField, true);
    }

    if (!this.value) {
      this.resetDetectionTechniques(); // set to default
      return;
    }

    this.formGroup.value = {
      applicationName: this.value.applicationName,
      processName: this.value.processName,
      exploitDetectionTechniques: convertDbValueToFormValue(this.value.exploitDetectionTechniques),
    };
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div class="rules-form-container">
        <gz-title fontWeight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}" class="form-subtitle"
          >${translateFn('Details')}</gz-title
        >
        ${this.renderDetailsFormFields()}

        <div class="title-with-button">
          <gz-title fontSize="${FontSizes.PARAGRAPH_11}" fontWeight="${FontWeights.SEMIBOLD}" class="form-subtitle"
            >${translateFn('Exploit detection techniques')}</gz-title
          >

          <gz-button
            type="${ButtonTypes.QUIET}"
            buttonHeight="${ButtonHeights.SMALL}"
            buttonFontSize="${ButtonFontSizes.SMALL}"
            @click="${this.resetDetectionTechniques.bind(this)}"
            >${translateFn('Reset to default')}
          </gz-button>
        </div>
        ${this.renderSettingsFormFields()}
      </div>
    `;
  }

  private renderDetailsFormFields(): TemplateResult {
    return html` <div class="rules-form w-full">
      <gz-form-field
        id="application-name-field"
        class="label-mw-250 mt-2"
        label="${translateFn('Application name')}"
        required
      >
        <gz-input
          inputWidth="${InputWidthValues.LARGE}"
          gz-form-input
          placeholder="${translateFn('Application name')}"
        ></gz-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>

      <gz-form-field class="label-mw-250" id="process-name-field" label="${translateFn('Process names')}" required>
        <gz-chips-generator-input
          gz-form-input
          hasFloatingLabel
          .strings="${{
            placeholder: translateFn('Process names'),
            notUniqueErrorMessage: translateFn('The entry already exists.'),
            hintText: translateFn('Separate process names by comma.'),
            clearListButtonText: translateFn('Clear list'),
            searchPlaceholder: translateFn('Search'),
            noSearchResultsString: translateFn('No results found'),
            deleteWindowTitle: translateFn('Delete process names?'),
            deleteWindowContentText: translateFn(
              'You are about to delete the process names from the list. This action cannot be undone.'
            ),
            cancelClearListBtnString: translateFn('Cancel'),
            clearListActionBtnString: translateFn('Delete'),
          }}"
        >
        </gz-chips-generator-input>
        <gz-input-error></gz-input-error>
      </gz-form-field>
    </div>`;
  }

  private renderSettingsFormFields(): TemplateResult {
    return html`
      <gz-form-field class="label-mw-250 mt-2" id="antimalware-anti-exploit-detection-techniques-field">
        <gz-selectable-table-row-group
          gz-form-input
          allowFullRowSelection
          allowFullColumnSelection
          .defaultColumn=${AntimalwareAntiExploitSystemWideDetectionsLinuxAction.REPORT_ONLY}
          .rows=${rows()}
          .columns=${columns()}
        ></gz-selectable-table-row-group>
      </gz-form-field>
    `;
  }

  private resetDetectionTechniques() {
    const defaultDetectionTechniques = getAntimalwareAntiExploitDetectionTechniquesDefaultValue(this.isCustomApp);
    this.exploitDetectionTechniquesField.value = convertDbValueToFormValue(defaultDetectionTechniques);
  }
}
